import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import {
  ProgramBundleNoUnfollowModal,
  type ProgramBundleNoUnfollowModalRef,
} from "@modules/programBundle/components/programBundleNoUnfollowModal"
import {
  ProgramBundlePauseOnOffModal,
  type ProgramBundlePauseOnOffModalRef,
} from "@modules/programBundle/components/programBundlePauseOnOffModal"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramOnOffProgression } from "@modules/programs/hooks/useProgramsOnOff"
import { ProgramProgressionStatus } from "@modules/programs/types/programOnOffTypes"
import { ProgramSubscriptionStatus, ProgramType } from "@modules/programs/types/programTypes"
import { HuProgramMiniCards } from "@modules/ui/components/cards/huProgramMiniCard"
import { getApproximatedFormattedNumber } from "@modules/utils/numberUtils"
import { type UseMutateAsyncFunction } from "@tanstack/react-query"
import { Check, PlusIcon } from "lucide-react"
import { useMemo, useRef, useState } from "react"

type HuProgramBundleMiniCardProps = {
  programId: string
  canUserFollowBundlePrograms: boolean
  followProgamsInProgramBundle: UseMutateAsyncFunction<void, Error, string[], unknown>
  unFollowProgamsInProgramBundle: UseMutateAsyncFunction<void, Error, string[], unknown>
  isFollowed: boolean | undefined
  className?: string
} & React.ComponentProps<"div">

export function HuProgramBundleMiniCard({
  programId,
  canUserFollowBundlePrograms,
  followProgamsInProgramBundle,
  unFollowProgamsInProgramBundle,
  isFollowed,
  className,
  ...props
}: HuProgramBundleMiniCardProps) {
  const [isPending, setIsPending] = useState(false)
  const programBundleNoUnfollowModalRef = useRef<ProgramBundleNoUnfollowModalRef>(null)
  const programBundlePauseOnOffModalRef = useRef<ProgramBundlePauseOnOffModalRef>(null)

  const { data: program, isLoading: isLoadingProgram } = useProgram(programId) // Consuming query to get the program subscription status
  const { approximatedFormattedNumber, approximatedNumber } = getApproximatedFormattedNumber(
    program.subscriptionCount ?? 0,
  )

  const { data: progression } = useProgramOnOffProgression(
    program.id,
    program
      ? program.type !== ProgramType.ONOFF ||
          !(
            program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
            program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
            program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE ||
            program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION
          )
      : false,
  )

  const isSubscribedInBothProgramAndBundle = useMemo(
    () =>
      // considering that this program is in the bundle
      canUserFollowBundlePrograms && program && program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE,
    [isLoadingProgram, program],
  )

  const handleClick = async () => {
    setIsPending(true)
    if (isSubscribedInBothProgramAndBundle) {
      // Tell the user that he can't unfollow the program because he is subscribed to it individually
      programBundleNoUnfollowModalRef.current?.open()
    } else if (
      isFollowed &&
      program.type === ProgramType.ONOFF &&
      progression &&
      progression.status === ProgramProgressionStatus.STARTED
    ) {
      // Tell the user that his progression will be paused if he unfollows the program
      programBundlePauseOnOffModalRef.current?.open()
    } else if (isFollowed) {
      await unFollowProgamsInProgramBundle([programId])
    } else {
      await followProgamsInProgramBundle([programId])
    }
    setIsPending(false)
  }

  if (!program) return null

  return (
    <div className={cn("flex w-full rounded-3xl bg-neutral-100", className)} {...props}>
      <HuProgramMiniCards item={program} editionAllowed className="h-40 min-h-40 w-40 min-w-40" />
      <div className="mx-4 my-2 flex grow flex-col gap-2">
        <div className="flex items-center justify-between">
          {canUserFollowBundlePrograms && isFollowed === true ? (
            <Button variant="primary" size="lg" onClick={() => handleClick()} disabled={isPending}>
              <Check color="currentColor" width={24} height={24} className="text-textDefault-heading" />
              {word("programBundle.follow.remove.button")}
            </Button>
          ) : canUserFollowBundlePrograms && isFollowed === false ? (
            <Button variant="outline" size="lg" onClick={() => handleClick()} disabled={isPending}>
              <PlusIcon color="currentColor" width={24} height={24} className="text-textDefault-heading" />
              {word("programBundle.follow.add.button")}
            </Button>
          ) : (
            <Text fontStyle="LM" className="flex text-textDefault-caption">
              {word("programBundle.info.programs.by", {
                firstname: program.owner?.firstname,
                lastname: program.owner?.lastname,
              })}
            </Text>
          )}
          <div className="flex flex-row items-baseline gap-1">
            <Text fontStyle="LL" className="flex whitespace-nowrap text-textDefault-heading">
              {approximatedFormattedNumber}
            </Text>
            <Text fontStyle="BS" className="flex text-textDefault-heading">
              {word(program.type === ProgramType.ONOFF ? "global.athlete" : "program.subcribers.name", {
                count: approximatedNumber !== 0 ? approximatedNumber : 1,
              })}
            </Text>
          </div>
        </div>

        <Text fontStyle="BM" className="line-clamp-4 text-textDefault-heading">
          {program.description}
        </Text>
      </div>

      <ProgramBundleNoUnfollowModal ref={programBundleNoUnfollowModalRef} programType={program.type} />
      <ProgramBundlePauseOnOffModal
        ref={programBundlePauseOnOffModalRef}
        onConfirmUnfollow={() => unFollowProgamsInProgramBundle([program.id])}
      />
    </div>
  )
}
